import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Sparkles } from 'lucide-react';
import { COLORS } from '../../utils/constants';
import BasicInfo from './steps/BasicInfo';
import UseCase from './steps/UseCase';
import ExperienceLevel from './steps/ExperienceLevel';
import ExpectedUsage from './steps/ExpectedUsage';
import WelcomeScreen from './steps/WelcomeScreen';

const OnboardingModal = ({ isOpen, onComplete, user }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState({
    name: user?.full_name || '',
    company: '',
    userType: '',
    useCases: [],
    mainGoal: '',
    experienceLevel: '',
    expectedResponses: ''
  });

  const totalSteps = 6; // Welcome + 4 steps + completion screen
  const [isCompleting, setIsCompleting] = useState(false);

  const handleNext = (stepData) => {
    setOnboardingData({ ...onboardingData, ...stepData });

    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // This is handled by the completion screen now
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      console.log('Onboarding data:', onboardingData);
      await onComplete(onboardingData);
    } catch (error) {
      console.error('Error saving onboarding data:', error);
      // Still close modal even if save fails
      onComplete(onboardingData);
    } finally {
      setIsCompleting(false);
    }
  };

  const CompletionScreen = () => (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        style={{
          width: '100px',
          height: '100px',
          margin: '0 auto 24px',
          background: `linear-gradient(135deg, ${COLORS.blue} 0%, #3B82F6 100%)`,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 20px 40px rgba(59, 130, 246, 0.3)'
        }}
      >
        <CheckCircle size={48} color="white" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h2 style={{
          fontSize: 'clamp(24px, 5vw, 32px)',
          fontWeight: '700',
          color: COLORS.black,
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          You're All Set! <Sparkles size={24} color={COLORS.blue} />
        </h2>
        <p style={{
          fontSize: 'clamp(14px, 3vw, 18px)',
          color: COLORS.gray,
          marginBottom: '32px',
          maxWidth: '500px',
          margin: '0 auto 32px',
          padding: '0 16px'
        }}>
          Welcome to Fylo! Let's start creating amazing forms that your audience will love to fill out.
        </p>

        <button
          onClick={handleComplete}
          disabled={isCompleting}
          style={{
            padding: '14px 32px',
            fontSize: 'clamp(14px, 2.5vw, 16px)',
            fontWeight: '600',
            color: 'white',
            backgroundColor: COLORS.blue,
            border: 'none',
            borderRadius: '12px',
            cursor: isCompleting ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
            opacity: isCompleting ? 0.6 : 1,
            width: '100%',
            maxWidth: '300px'
          }}
          onMouseEnter={(e) => {
            if (!isCompleting) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 12px 32px rgba(59, 130, 246, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.3)';
          }}
        >
          {isCompleting ? 'Setting up...' : 'Start Creating Forms'}
        </button>
      </motion.div>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeScreen onNext={() => setCurrentStep(1)} user={user} />;
      case 1:
        return <BasicInfo data={onboardingData} onNext={handleNext} onBack={handleBack} />;
      case 2:
        return <UseCase data={onboardingData} onNext={handleNext} onBack={handleBack} />;
      case 3:
        return <ExperienceLevel data={onboardingData} onNext={handleNext} onBack={handleBack} />;
      case 4:
        return <ExpectedUsage data={onboardingData} onNext={handleNext} onBack={handleBack} />;
      case 5:
        return <CompletionScreen />;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={(e) => {
        // Prevent closing when clicking outside (onboarding is important)
        e.stopPropagation();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        style={{
          backgroundColor: COLORS.white,
          borderRadius: '12px',
          maxWidth: '850px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          position: 'relative',
          margin: '0 16px'
        }}
      >
        {/* Progress Bar */}
        {currentStep > 0 && (
          <div
            style={{
              height: '4px',
              backgroundColor: COLORS.lightGray,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep) / (totalSteps - 1)) * 100}%` }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              style={{
                height: '100%',
                backgroundColor: COLORS.blue,
                borderRadius: '4px'
              }}
            />
          </div>
        )}

        {/* Skip Button - hide on welcome and completion screens */}
        {currentStep > 0 && currentStep < totalSteps - 1 && (
          <button
            onClick={handleSkip}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'none',
              border: 'none',
              color: COLORS.gray,
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              padding: '8px 16px',
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              zIndex: 10
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = COLORS.lightGray;
              e.target.style.color = COLORS.darkGray;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = COLORS.gray;
            }}
          >
            Skip
          </button>
        )}

        {/* Content */}
        <div
          style={{
            padding: currentStep === 0 ? '0' : 'clamp(24px, 5vw, 48px) clamp(20px, 5vw, 60px)',
            overflowY: 'auto',
            maxHeight: currentStep === 0 ? '90vh' : 'calc(90vh - 4px)'
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingModal;
